/**
 * Protected Route Component
 *
 * Wraps routes that require authentication and/or specific role access.
 * Redirects to login if not authenticated, or shows access denied for insufficient permissions.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, hasMinimumRole, type Role } from '../../contexts/AuthContext';

// =============================================================================
// Types
// =============================================================================

interface ProtectedRouteProps {
  /** The protected content to render */
  children: React.ReactNode;
  /** Minimum role required to access this route */
  requiredRole?: Role;
  /** Specific roles that can access this route */
  allowedRoles?: Role[];
  /** Redirect path when not authenticated (default: /login) */
  loginPath?: string;
  /** Redirect path when access denied (default: /dashboard) */
  accessDeniedPath?: string;
  /** Optional custom loading component */
  loadingComponent?: React.ReactNode;
  /** Optional custom access denied component */
  accessDeniedComponent?: React.ReactNode;
}

// =============================================================================
// Components
// =============================================================================

/**
 * Default loading spinner component
 */
function DefaultLoadingSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-discord-blurple border-t-transparent" />
        <p className="text-gray-400">Verifying authentication...</p>
      </div>
    </div>
  );
}

/**
 * Default access denied component
 */
function DefaultAccessDenied() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <div className="mb-4 text-6xl">
          <span role="img" aria-label="lock">
            🔒
          </span>
        </div>
        <h1 className="mb-2 text-2xl font-bold text-red-500">Access Denied</h1>
        <p className="text-gray-400">
          You don't have permission to access this page.
        </p>
      </div>
    </div>
  );
}

/**
 * Protected Route wrapper component
 *
 * @example
 * // Require authentication only
 * <ProtectedRoute>
 *   <DashboardPage />
 * </ProtectedRoute>
 *
 * @example
 * // Require minimum TENANT_ADMIN role
 * <ProtectedRoute requiredRole="TENANT_ADMIN">
 *   <AdminPage />
 * </ProtectedRoute>
 *
 * @example
 * // Allow only specific roles
 * <ProtectedRoute allowedRoles={['GLOBAL_ADMIN', 'TENANT_ADMIN']}>
 *   <ManagementPage />
 * </ProtectedRoute>
 */
export function ProtectedRoute({
  children,
  requiredRole,
  allowedRoles,
  loginPath = '/login',
  accessDeniedPath,
  loadingComponent,
  accessDeniedComponent,
}: ProtectedRouteProps) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return <>{loadingComponent ?? <DefaultLoadingSpinner />}</>;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    // Preserve the attempted URL to redirect back after login
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // Check role-based access
  const hasAccess = checkAccess(user.role, requiredRole, allowedRoles);

  if (!hasAccess) {
    // If accessDeniedPath is provided, redirect there
    if (accessDeniedPath) {
      return <Navigate to={accessDeniedPath} replace />;
    }

    // Otherwise, show access denied component
    return <>{accessDeniedComponent ?? <DefaultAccessDenied />}</>;
  }

  // User is authenticated and authorized
  return <>{children}</>;
}

/**
 * Check if user role has access based on requirements
 */
function checkAccess(
  userRole: Role,
  requiredRole?: Role,
  allowedRoles?: Role[]
): boolean {
  // If no role requirements, allow access
  if (!requiredRole && !allowedRoles) {
    return true;
  }

  // Check if user role is in allowed roles list
  if (allowedRoles && allowedRoles.length > 0) {
    return allowedRoles.includes(userRole);
  }

  // Check if user has minimum required role
  if (requiredRole) {
    return hasMinimumRole({ role: userRole } as { id: string; email: string; name: string; role: Role; tenantId: string | null }, requiredRole);
  }

  return true;
}

// =============================================================================
// Convenience Wrappers
// =============================================================================

interface SimpleProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Route that requires GLOBAL_ADMIN role
 */
export function GlobalAdminRoute({ children }: SimpleProtectedRouteProps) {
  return (
    <ProtectedRoute requiredRole="GLOBAL_ADMIN">{children}</ProtectedRoute>
  );
}

/**
 * Route that requires at least TENANT_ADMIN role
 */
export function TenantAdminRoute({ children }: SimpleProtectedRouteProps) {
  return (
    <ProtectedRoute requiredRole="TENANT_ADMIN">{children}</ProtectedRoute>
  );
}

/**
 * Route that requires at least TENANT_USER role
 */
export function TenantUserRoute({ children }: SimpleProtectedRouteProps) {
  return (
    <ProtectedRoute requiredRole="TENANT_USER">{children}</ProtectedRoute>
  );
}

/**
 * Route that requires authentication only (any role)
 */
export function AuthenticatedRoute({ children }: SimpleProtectedRouteProps) {
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

export default ProtectedRoute;
